import React, { useEffect, useMemo, useState } from "react";
import { DatePicker, Modal, Tooltip } from "antd";
import { EditOutlined } from "@ant-design/icons";
import zhCN from "antd/es/date-picker/locale/zh_CN";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/zh-cn";
import {
  CRDHolidayItem,
  getCRDRangeText,
  getHolidayType,
  getOrderLineKey,
  isDateInCRDRange,
  OrderPreviewProduct,
} from "Utils/orderPreviewCRD";

dayjs.locale("zh-cn");

interface OrderPreviewCRDModalProps {
  open: boolean;
  currentProduct?: OrderPreviewProduct | null;
  products: OrderPreviewProduct[];
  holidays?: CRDHolidayItem[] | string[];
  onClose: () => void;
  onSubmit: (payload: { newCrd: string; selectedLinesMap: Record<string, string> }) => void;
}

const OrderPreviewCRDModal: React.FC<OrderPreviewCRDModalProps> = ({
  open,
  currentProduct,
  products,
  holidays = [],
  onClose,
  onSubmit,
}) => {
  const [newCrd, setNewCrd] = useState("");
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  useEffect(() => {
    if (!open || !currentProduct) {
      setNewCrd("");
      setSelectedKeys([]);
      return;
    }
    setNewCrd(currentProduct.crd || currentProduct.CRD_Range?.defaultDate || "");
    setSelectedKeys([]);
  }, [open, currentProduct]);

  const lineList = useMemo(() => {
    if (!currentProduct) return [];
    const currentKey = getOrderLineKey(currentProduct);
    return products.filter((product) => {
      const productKey = getOrderLineKey(product);
      return (
        productKey !== currentKey &&
        !product.freeGoodItem &&
        product.CRD_Range &&
        product.CRD_Range.status === "eligible"
      );
    });
  }, [currentProduct, products]);

  const selectableKeys = useMemo(
    () =>
      lineList
        .filter((product) => isDateInCRDRange(product.CRD_Range, newCrd, holidays))
        .map((product) => getOrderLineKey(product)),
    [holidays, lineList, newCrd]
  );

  useEffect(() => {
    setSelectedKeys((prev) => prev.filter((key) => selectableKeys.includes(key)));
  }, [selectableKeys]);

  const allChecked = selectableKeys.length > 0 && selectedKeys.length === selectableKeys.length;

  const disabledDate = (current: Dayjs) => {
    if (!currentProduct) return false;
    return !isDateInCRDRange(currentProduct.CRD_Range, current.format("YYYY-MM-DD"), holidays);
  };

  const handleSubmit = () => {
    if (!currentProduct || !newCrd) return;
    const selectedLinesMap = selectedKeys.reduce((prev, key) => {
      prev[key] = newCrd;
      return prev;
    }, {} as Record<string, string>);
    selectedLinesMap[getOrderLineKey(currentProduct)] = newCrd;
    onSubmit({
      newCrd,
      selectedLinesMap,
    });
  };

  const renderDateCell = (current: any, info: any) => {
    if (info.type !== "date") {
      return info.originNode;
    }
    const holidayType = getHolidayType(current.format("YYYY-MM-DD"), holidays);
    if (!holidayType) {
      return info.originNode;
    }
    return (
      <div className="crdDatePickerPopup__holiday-day">
        {info.originNode}
        <span
          className={`crdDatePickerPopup__holiday-tag ${
            holidayType === "transfer_workday" ? "workday" : "holiday"
          }`}
        >
          {holidayType === "transfer_workday" ? "班" : "休"}
        </span>
      </div>
    );
  };

  return (
    <Modal
      open={open && !!currentProduct}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={520}
      destroyOnHidden
      className="orderPreviewCRDModal"
      maskClosable
    >
      {currentProduct && (
        <>
          <div className="modify-crd-dialog__title" id="edit-crd-dialog-title">
            <div className="modify-crd-dialog__title-wrap">
              <span className="modify-crd-dialog__title-text">修改期望发货时间</span>
            </div>
          </div>

          <div className="modify-crd-dialog__divider" />

          <div className="modify-crd-dialog__content">
            <div className="modify-crd-dialog__info-block">
              <div className="modify-crd-dialog__info-row">
                <div className="modify-crd-dialog__info-item">
                  <span className="modify-crd-dialog__info-label">货号</span>
                  <span className="modify-crd-dialog__info-value">{currentProduct.sku}</span>
                </div>
              </div>
              <div className="modify-crd-dialog__info-row">
                <div className="modify-crd-dialog__info-item">
                  <span className="modify-crd-dialog__info-label">产品名称</span>
                  <span
                    className="modify-crd-dialog__info-value"
                    dangerouslySetInnerHTML={{
                      __html: currentProduct.product?.productName || currentProduct.productName || "",
                    }}
                  />
                </div>
              </div>
              <div className="modify-crd-dialog__info-row">
                <div className="modify-crd-dialog__info-item">
                  <span className="modify-crd-dialog__info-label">当前期望发货日期</span>
                  <span className="modify-crd-dialog__info-value">{currentProduct.crd || ""}</span>
                </div>
              </div>
            </div>

            <div className="modify-crd-dialog__field-label">
              新期望发货日期
              <span className="modify-crd-dialog__required">*</span>
            </div>
            <DatePicker
              className="modify-crd-dialog__date-field"
              popupClassName="crdDatePickerPopup"
              value={newCrd ? dayjs(newCrd) : null}
              format="YYYY-MM-DD"
              locale={zhCN}
              allowClear={false}
              placement="bottomLeft"
              getPopupContainer={() => document.body}
              popupStyle={{ zIndex: 1080 }}
              disabledDate={disabledDate}
              cellRender={renderDateCell}
              onChange={(value) => setNewCrd(value ? value.format("YYYY-MM-DD") : "")}
            />

            {lineList.length > 0 && (
              <>
                <div className="modify-crd-dialog__field-label">选择要同时修改的行</div>

                <div className="modify-crd-dialog__table-wrap">
                  <div className="modify-crd-dialog__table-head">
                    <span className="modify-crd-dialog__table-check">
                      <input
                        type="checkbox"
                        className="modify-crd-dialog__checkbox"
                        checked={allChecked}
                        disabled={selectableKeys.length === 0}
                        onChange={(event) => {
                          setSelectedKeys(event.target.checked ? selectableKeys : []);
                        }}
                      />
                    </span>
                    <span>货号</span>
                    <span>当前期望发货日期</span>
                  </div>

                  {lineList.map((product) => {
                    const key = getOrderLineKey(product);
                    const inRange = isDateInCRDRange(product.CRD_Range, newCrd, holidays);
                    const rangeText = getCRDRangeText(product.CRD_Range);
                    const rowClassName = `modify-crd-dialog__table-row ${
                      !inRange ? "disabled modify-crd-dialog__table-row--tooltip" : ""
                    }`;

                    if (!inRange && rangeText) {
                      return (
                        <Tooltip
                          key={key}
                          title={rangeText}
                          placement="top"
                          overlayClassName="modify-crd-dialog__tooltip"
                        >
                          <div className={rowClassName}>
                            <span className="modify-crd-dialog__table-check">
                              <input
                                type="checkbox"
                                className="modify-crd-dialog__checkbox"
                                checked={selectedKeys.includes(key)}
                                disabled
                                onChange={() => undefined}
                              />
                            </span>
                            <span>{product.sku}</span>
                            <span>{product.crd || "-"}</span>
                          </div>
                        </Tooltip>
                      );
                    }

                    return (
                      <div key={key} className={rowClassName}>
                        <span className="modify-crd-dialog__table-check">
                          <input
                            type="checkbox"
                            className="modify-crd-dialog__checkbox"
                            checked={selectedKeys.includes(key)}
                            disabled={!inRange}
                            onChange={() => {
                              setSelectedKeys((prev) =>
                                prev.includes(key)
                                  ? prev.filter((item) => item !== key)
                                  : [...prev, key]
                              );
                            }}
                          />
                        </span>
                        <span>{product.sku}</span>
                        <span>{product.crd || "-"}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <div className="modify-crd-dialog__footer">
            <div className="modify-crd-dialog__actions">
              <button
                type="button"
                className="modify-crd-dialog__btn modify-crd-dialog__btn-cancel"
                onClick={onClose}
              >
                取消
              </button>
              <button
                type="button"
                className="modify-crd-dialog__btn modify-crd-dialog__btn-submit"
                onClick={handleSubmit}
                disabled={!newCrd}
              >
                确认
              </button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export const CRDEditTrigger: React.FC<{
  value?: string;
  onClick: () => void;
  mobile?: boolean;
}> = ({ value, onClick, mobile = false }) => (
  <span className={`crdEditTrigger ${mobile ? "mobile" : ""}`} onClick={onClick}>
    <span className="crdEditTrigger__value">{value || "-"}</span>
    <EditOutlined className="crdEditTrigger__icon" />
  </span>
);

export default OrderPreviewCRDModal;
